import React, { useCallback, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import {
  getAIStatus, enableAI, disableAI, setAIPaper, updateAIConfig, resetAIConfig, runAIBacktest,
} from '../../api/client';

// v4 Playbook settings. [key, label, help]
const TOGGLES = [
  ['trend_enabled',     'Trend-day debit spread',      'Breakout of a wide opening range'],
  ['butterfly_enabled', 'Sideways-expiry butterfly',   'Long ATM butterfly at 13:15 on expiry day'],
];
const FIELDS = [
  ['lots',               'Lots per leg',                          '1 lot = 65 qty'],
  ['or_min_width_atr',   'Trend: min opening range (× daily ATR)', 'Default 0.6'],
  ['spread_width',       'Trend: spread width (points)',           'Buy ATM, sell this far OTM'],
  ['trail_atr',          'Trend: trailing stop (× hourly ATR)',    'Default 1.0'],
  ['fly_max_range_atr',  'Butterfly: max 09:15–13:15 range (× daily ATR)', 'Default 0.6'],
  ['fly_width',          'Butterfly: wing width (points)',         'Default 150'],
  ['fly_stop_pct',       'Butterfly: stop at −% of debit',        'Default 50'],
  ['exit_time',          'Exit any open structure at (HH:MM)',     'Default 15:15'],
  ['backtest_cost_per_leg', 'Backtest cost per leg (points)',      'Raise to stress-test'],
];

const card = {
  background: 'var(--card, #1a1a1a)', border: '1px solid var(--border, #333)',
  borderRadius: 12, padding: 20, marginBottom: 16,
};
const heading = { fontSize: 13, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 };
const tile = { background: '#222', borderRadius: 8, padding: '8px 12px' };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 };
const inr = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`);
const pnlColor = (v) => (v == null ? '#aaa' : v >= 0 ? '#22c55e' : '#ef4444');
const num = (v, d = 1) => (v == null || v === '' ? '—' : Number(v).toFixed(d));

function Tile({ label, value, color = '#fff' }) {
  return (
    <div style={tile}>
      <div style={{ fontSize: 11, color: '#777' }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

export default function AITrading() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');

  const [btFrom, setBtFrom] = useState('');
  const [btTo, setBtTo] = useState('');
  const [btRunning, setBtRunning] = useState(false);
  const [btResult, setBtResult] = useState(null);
  const [btError, setBtError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await getAIStatus();
      setStatus(res.data);
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load AI status');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  // Seed the settings form once from the first status that carries config.
  useEffect(() => {
    if (status?.config && draft === null) {
      const d = {};
      [...TOGGLES, ...FIELDS].forEach(([k]) => { d[k] = status.config[k]; });
      setDraft(d);
    }
  }, [status, draft]);

  const act = async (fn, msg) => {
    setBusy(true); setError('');
    try { await fn(); await load(); } catch (e) { setError(e.response?.data?.detail || msg); }
    finally { setBusy(false); }
  };

  const toggleEnable = () => act(() => (status?.enabled ? disableAI() : enableAI()), 'Toggle failed');
  const togglePaper = () => {
    if (status?.paper && !window.confirm('⚠️ Switch AI Trading to LIVE? Real multi-leg orders will be placed.')) return;
    act(() => setAIPaper(!status.paper), 'Mode toggle failed');
  };
  const saveConfig = async () => {
    setSaving(true); setError(''); setSavedMsg('');
    try {
      await updateAIConfig(draft);
      setSavedMsg('Saved'); setTimeout(() => setSavedMsg(''), 3000);
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const resetConfig = async () => {
    if (!window.confirm('Reset AI Trading settings to the researched defaults?')) return;
    setSaving(true);
    try {
      await resetAIConfig();
      setDraft(null);
      await load();
      setSavedMsg('Reset to defaults'); setTimeout(() => setSavedMsg(''), 3000);
    } catch (e) { setError(e.response?.data?.detail || 'Reset failed'); }
    finally { setSaving(false); }
  };
  const runBacktest = async () => {
    if (!btFrom || !btTo) { setBtError('Pick a from and to date.'); return; }
    setBtRunning(true); setBtError(''); setBtResult(null);
    try {
      // Current form values apply to this run only (no need to save first).
      const res = await runAIBacktest({ from_date: btFrom, to_date: btTo, config_overrides: draft || {} });
      setBtResult(res.data);
    } catch (e) { setBtError(e.response?.data?.detail || 'Backtest failed'); }
    finally { setBtRunning(false); }
  };

  const plan = status?.plan || {};
  const s = status?.structure;
  const last = status?.last_trade;

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 4px 0', fontSize: 22, fontWeight: 700 }}>🤖 AI Trading — Playbook</h2>
        <p style={{ margin: 0, color: '#888', fontSize: 13 }}>
          One defined-risk NIFTY option-buying trade on trend days and sideways expiry days · stands aside otherwise
        </p>
      </div>

      {error && <div style={{ ...card, borderColor: '#ef4444', color: '#ef4444' }}>{error}</div>}
      {status?.available && status.engine_running === false && (
        <div style={{ ...card, color: '#f59e0b' }}>
          ⚠️ Strategy engine isn't running — connect Fyers. Settings can be changed now; decisions start once it's live.
        </div>
      )}
      {status?.engine_running && status.fyers_connected === false && (
        <div style={{ ...card, color: '#f59e0b' }}>⚠️ Fyers not connected — no market data yet.</div>
      )}

      {/* Controls */}
      <div style={{ ...card, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={toggleEnable} disabled={busy} style={{
          padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600,
          fontSize: 14, background: status?.enabled ? '#ef4444' : '#22c55e', color: '#fff',
        }}>
          {status?.enabled ? '■ Disable AI Trading' : '▶ Enable AI Trading'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: '#aaa' }}>Mode:</span>
          <button onClick={togglePaper} disabled={busy} style={{
            padding: '6px 14px', borderRadius: 99, border: '1px solid', background: 'transparent',
            cursor: 'pointer', fontSize: 13, fontWeight: 600,
            borderColor: status?.paper ? '#3b82f6' : '#ef4444', color: status?.paper ? '#3b82f6' : '#ef4444',
          }}>
            {status?.paper ? '📝 Paper' : '🔴 LIVE'}
          </button>
        </div>
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#aaa' }}>
          Day P&L: <b style={{ color: pnlColor(status?.daily_pnl) }}>{inr(status?.daily_pnl ?? 0)}</b>
        </div>
      </div>

      {/* Today's plan */}
      <div style={card}>
        <div style={heading}>Today</div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14, color: s ? '#22c55e' : '#fff' }}>
          {plan.status || '—'}
        </div>
        <div style={grid}>
          <Tile label="Expiry day" value={plan.is_expiry == null ? '—' : plan.is_expiry ? 'Yes' : 'No'} />
          <Tile label="Daily ATR (prev day)" value={num(plan.daily_atr, 0)} />
          <Tile label="Opening range (09:15–10:15)"
                value={plan.or_high ? `${num(plan.or_low, 0)} – ${num(plan.or_high, 0)}` : '—'} />
          <Tile label="OR width / needed for trend"
                value={plan.or_width ? `${num(plan.or_width, 0)} / ${num(plan.or_needed, 0)}` : '—'}
                color={plan.or_width && plan.or_width >= plan.or_needed ? '#22c55e' : '#fff'} />
          <Tile label="Range so far / butterfly max"
                value={plan.range_so_far ? `${num(plan.range_so_far, 0)} / ${num(plan.fly_max, 0)}` : '—'}
                color={plan.is_expiry && plan.range_so_far && plan.range_so_far <= plan.fly_max ? '#22c55e' : '#fff'} />
          <Tile label="Hourly candles closed" value={plan.bars ?? '—'} />
        </div>
      </div>

      {/* Open structure */}
      <div style={card}>
        <div style={heading}>Open structure</div>
        {s ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{s.label}</div>
            <div style={{ fontSize: 13, color: '#aaa', margin: '4px 0 14px' }}>
              {s.reason} · entered {s.entered_at?.slice(11, 16)}
            </div>
            <div style={grid}>
              <Tile label="Debit (per unit)" value={num(s.debit, 2)} />
              <Tile label="Value now" value={num(s.value, 2)} />
              <Tile label="Unrealised P&L" value={inr(s.unrealized_pnl)} color={pnlColor(s.unrealized_pnl)} />
              <Tile label="Stop" value={s.stop_text} color="#f59e0b" />
            </div>
            <div style={{ overflowX: 'auto', marginTop: 14 }}>
              <table style={{ width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ color: '#777', textAlign: 'left' }}>
                  <th style={{ padding: 6 }}>Leg</th><th>Side</th><th>Lots</th><th>Entry</th><th>LTP</th>
                </tr></thead>
                <tbody>
                  {s.legs.map((l) => (
                    <tr key={l.symbol} style={{ borderTop: '1px solid #2a2a2a' }}>
                      <td style={{ padding: 6 }}>{l.symbol.split(':')[1]}</td>
                      <td style={{ color: l.lots > 0 ? '#22c55e' : '#ef4444' }}>{l.lots > 0 ? 'BUY' : 'SELL'}</td>
                      <td>{Math.abs(l.lots)}</td><td>{num(l.entry, 2)}</td><td>{num(l.ltp, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div style={{ color: '#888', fontSize: 13 }}>
            {status?.traded_today ? 'Already traded today — at most one structure per day.' : 'None.'}
          </div>
        )}
      </div>

      {/* Last trade */}
      {last && (
        <div style={card}>
          <div style={heading}>Last trade</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>{last.label} · {last.date}</div>
              <div style={{ fontSize: 13, color: '#aaa' }}>
                {last.entered_at?.slice(11, 16)} → {last.exited_at?.slice(11, 16)} · exit: {last.exit_reason}
              </div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: pnlColor(last.pnl) }}>{inr(last.pnl)}</div>
          </div>
        </div>
      )}

      {/* How it works */}
      <div style={card}>
        <div style={heading}>How it trades</div>
        <div style={{ fontSize: 13, color: '#ccc', lineHeight: 1.7 }}>
          <b>Trend day</b> — the first hourly candle (09:15–10:15) is wide (≥ 0.6× daily ATR) and a later hourly
          candle (by 12:15) is the first to close outside it → <b>debit spread</b> in that direction (buy ATM, sell
          200 OTM). Exit: NIFTY back to the opening-range middle (trailing 1× hourly ATR), or 15:15.<br />
          <b>Sideways expiry day</b> — 09:15–13:15 stayed within 0.6× daily ATR → <b>long ATM butterfly</b> at 13:15
          (gains as expiry-day time value collapses while NIFTY stays near the strike). Exit: −50% of debit or 15:15.<br />
          <b>Anything else</b> — no trade. Expect roughly one trade a week. Every trade is net-debit, defined-risk.<br />
          <span style={{ color: '#888' }}>
            Backtest on 3 years of NIFTY data (calibrated to your real fills): profitable each year, but 2025 was thin,
            and the trend spread alone is volatile. Paper-trade it before going live.
          </span>
        </div>
      </div>

      {/* Settings */}
      {draft && (
        <div style={card}>
          <div style={heading}>Settings</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 14 }}>
            {TOGGLES.map(([k, label, help]) => (
              <label key={k} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }} title={help}>
                <input type="checkbox" checked={!!draft[k]}
                       onChange={(e) => setDraft({ ...draft, [k]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            {FIELDS.map(([k, label, help]) => (
              <label key={k} style={{ fontSize: 12, color: '#aaa' }}>
                {label}
                <input value={draft[k] ?? ''} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
                       style={{ marginTop: 4 }} />
                <span style={{ fontSize: 11, color: '#666' }}>{help}</span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 16 }}>
            <button onClick={saveConfig} disabled={saving} style={{
              padding: '8px 18px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff',
              fontWeight: 600, cursor: 'pointer',
            }}>Save settings</button>
            <button onClick={resetConfig} disabled={saving} style={{
              padding: '8px 18px', borderRadius: 8, border: '1px solid #555', background: 'transparent',
              color: '#ccc', cursor: 'pointer',
            }}>Reset to defaults</button>
            {savedMsg && <span style={{ color: '#22c55e', fontSize: 13 }}>{savedMsg}</span>}
          </div>
        </div>
      )}

      {/* Backtest */}
      <div style={card}>
        <div style={heading}>Backtest</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
          Replays the playbook on hourly NIFTY candles (up to 400 days) with the current form values. Option prices
          are modelled (trading-time Black-Scholes, IV = previous day's VIX × 0.9, plus a per-leg cost) — calibrated
          to real fills, but still an estimate.
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: '#aaa' }}>From
            <input type="date" value={btFrom} onChange={(e) => setBtFrom(e.target.value)} />
          </label>
          <label style={{ fontSize: 12, color: '#aaa' }}>To
            <input type="date" value={btTo} onChange={(e) => setBtTo(e.target.value)} />
          </label>
          <button onClick={runBacktest} disabled={btRunning} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none', background: '#8b5cf6', color: '#fff',
            fontWeight: 600, cursor: 'pointer', height: 38,
          }}>{btRunning ? 'Running…' : '▶ Run backtest'}</button>
        </div>
        {btError && <div style={{ color: '#ef4444', marginTop: 10, fontSize: 13 }}>{btError}</div>}

        {btResult && (
          <div style={{ marginTop: 18 }}>
            {btResult.warnings && (
              <div style={{ color: '#f59e0b', fontSize: 12, marginBottom: 10 }}>⚠️ {btResult.warnings.note}</div>
            )}
            <div style={grid}>
              <Tile label="Sessions" value={btResult.params?.sessions ?? '—'} />
              <Tile label="Trades" value={btResult.summary.trades} />
              <Tile label="Win rate" value={`${btResult.summary.win_rate}%`} />
              <Tile label="Total P&L" value={inr(btResult.summary.total_pnl)} color={pnlColor(btResult.summary.total_pnl)} />
              <Tile label="Profit factor" value={btResult.summary.profit_factor ?? '—'} />
              <Tile label="Max drawdown" value={inr(btResult.summary.max_drawdown)} color="#ef4444" />
            </div>

            {btResult.equity_curve?.length > 1 && (
              <div style={{ height: 200, marginTop: 16 }}>
                <ResponsiveContainer>
                  <LineChart data={btResult.equity_curve}>
                    <XAxis dataKey="time" tickFormatter={(t) => t.slice(5, 10)} stroke="#666" fontSize={11} />
                    <YAxis stroke="#666" fontSize={11} />
                    <Tooltip formatter={(v) => inr(v)} labelFormatter={(t) => t.slice(0, 16)}
                             contentStyle={{ background: '#111', border: '1px solid #333' }} />
                    <ReferenceLine y={0} stroke="#444" />
                    <Line type="monotone" dataKey="cum_pnl" stroke="#8b5cf6" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginTop: 16 }}>
              <div>
                <div style={{ ...heading, fontSize: 12 }}>By setup</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr style={{ color: '#777', textAlign: 'left' }}>
                    <th style={{ padding: 4 }}>Setup</th><th>Trades</th><th>Win%</th><th>P&L</th><th>PF</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(btResult.by_setup).map(([k, v]) => (
                      <tr key={k} style={{ borderTop: '1px solid #2a2a2a' }}>
                        <td style={{ padding: 4 }}>{k === 'trend' ? 'Trend spread' : 'Butterfly'}</td>
                        <td>{v.trades}</td><td>{v.win_rate}</td>
                        <td style={{ color: pnlColor(v.total_pnl) }}>{inr(v.total_pnl)}</td>
                        <td>{v.profit_factor ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div style={{ ...heading, fontSize: 12 }}>By month</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {Object.entries(btResult.by_month).map(([m, v]) => (
                      <tr key={m} style={{ borderTop: '1px solid #2a2a2a' }}>
                        <td style={{ padding: 4 }}>{m}</td><td>{v.trades} trades</td>
                        <td style={{ color: pnlColor(v.pnl), textAlign: 'right' }}>{inr(v.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ ...heading, fontSize: 12, marginTop: 18 }}>Trades</div>
            <div style={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
              <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ color: '#777', textAlign: 'left' }}>
                  <th style={{ padding: 4 }}>Day</th><th>Setup</th><th>In → Out</th><th>Debit</th>
                  <th>Exit value</th><th>Exit</th><th style={{ textAlign: 'right' }}>P&L</th>
                </tr></thead>
                <tbody>
                  {[...btResult.trades].reverse().map((t, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #2a2a2a' }}>
                      <td style={{ padding: 4 }}>{t.day}</td>
                      <td>{t.setup === 'butterfly' ? 'Butterfly' : t.direction > 0 ? 'Bull spread' : 'Bear spread'}</td>
                      <td>{t.entry_time.slice(11, 16)} → {t.exit_time.slice(11, 16)}</td>
                      <td>{num(t.debit, 1)}</td><td>{num(t.exit_value, 1)}</td><td>{t.exit_reason}</td>
                      <td style={{ textAlign: 'right', color: pnlColor(t.pnl) }}>{inr(t.pnl)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
